/**
 * layout.ts
 *
 * Grid layout calculation for PlatinumTable component.
 * Converts PlatinumData into a 2D grid structure with proper rowspan/colspan values.
 */

import type { PlatinumData } from '../../types/carbon/models/PlatinumData'
import type { PlatinumVec } from '../../types/carbon/models/PlatinumVec'
import type { PlatinumProperties } from '../../types/carbon/models/PlatinumProperties'
import type { XDecimals } from '../../types/carbon/models/XDecimals'
import type { PVecType } from '../../types/carbon/models/PVecType'

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Maximum rowspan for cells in production mode.
 * Cells with larger rowspan will be split to improve virtualized rendering.
 * Must be <= overscan in PlatinumTable.tsx to prevent white gaps during scrolling.
 */
const MAX_ROWSPAN = 5

/**
 * Maximum colspan for cells in production mode.
 * Cells with larger colspan will be split to improve virtualized rendering.
 */
const MAX_COLSPAN = 5

// ============================================================================
// EMPTY COLUMN DETECTION
// ============================================================================

/**
 * Information about empty columns and how to adjust coordinates
 */
interface ColumnAdjustment {
  emptyColumns: Set<number>;  // Set of column indices that are empty
  columnMapping: Map<number, number>;  // Original col index -> adjusted col index
}

/**
 * Identify empty group columns in the side header.
 * A column is considered empty if all groups spanning it have whitespace-only text.
 *
 * @param groups - Side groups from PlatinumData
 * @param sideThickness - Number of group columns
 * @returns Information about which columns are empty and how to map coordinates
 */
function identifyEmptyGroupColumns(
  groups: Array<{ x?: number; y?: number; w?: number; h?: number; text?: string | null }> | null | undefined,
  sideThickness: number,
  debug: boolean = false
): ColumnAdjustment {
  const emptyColumns = new Set<number>()
  const columnMapping = new Map<number, number>()

  if(debug) {
    return { emptyColumns, columnMapping }
  }

  if (!groups || groups.length === 0 || sideThickness === 0) {
    // No groups means no empty columns to filter
    for (let col = 0; col < sideThickness; col++) {
      columnMapping.set(col, col)
    }
    return { emptyColumns, columnMapping }
  }

  // Check each group column for emptiness
  for (let col = 0; col < sideThickness; col++) {
    // Find all groups that span this column
    const groupsInColumn = groups.filter(g => {
      const x = g.x ?? 0
      const w = g.w ?? 1
      return x <= col && x + w > col
    })

    // Column is empty if:
    // 1. No groups span it, OR
    // 2. All groups spanning it have blank/whitespace-only text
    const isEmpty = groupsInColumn.length === 0 ||
      groupsInColumn.every(g => !g.text || g.text.trim() === '')

    if (isEmpty) {
      emptyColumns.add(col)
    }
  }

  // Build column mapping (original index -> adjusted index)
  let shift = 0
  for (let col = 0; col < sideThickness; col++) {
    if (emptyColumns.has(col)) {
      shift++
      // Empty columns don't get a mapping
    } else {
      columnMapping.set(col, col - shift)
    }
  }

  return { emptyColumns, columnMapping }
}

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Position information for a cell
 */
export interface CellPosition {
  rowIdx: number;
  colIdx: number;
  isTopHeader: boolean;
  isSideHeader: boolean;
  isCorner: boolean;
  isFirstInVector: boolean;  // First band in a side vector (for border rendering)
  isLastInVector: boolean;   // Last band in a side vector (for border rendering)
  isFirstColumnInTopVector: boolean;  // First column in a top vector (for horizontal vector borders)
  isLastColumnInTopVector: boolean;   // Last column in a top vector (for horizontal vector borders)
  neighbors: {
    above: GridCell['type'] | null;
    below: GridCell['type'] | null;
    left: GridCell['type'] | null;
    right: GridCell['type'] | null;
  };
}

/**
 * Hierarchy context for a cell (for provenance tracking)
 */
export interface CellHierarchy {
  topVecIndex?: number;   // Which column vector (index into data.top.vecs)
  sideVecIndex?: number;  // Which row vector (index into data.side.vecs)
  bandIndex?: number;     // Which band within the vector (0-based)
}

/**
 * Split cell tracking information
 */
export interface SplitInfo {
  originalCellId: string;  // UUID of the original cell before splitting
  isFirstRow: boolean;     // First row of splits
  isLastRow: boolean;      // Last row of splits
  isFirstCol: boolean;     // First column of splits
  isLastCol: boolean;      // Last column of splits
}

/**
 * A single cell in the 2D grid with span information
 */
export interface GridCell {
  content: string;
  rowspan: number;
  colspan: number;
  type: 'title' | 'group' | 'label' | 'letter' | 'data' | 'empty' | 'occupied';
  // Original coordinates from PlatinumData
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  // Band info for data cells
  bandStyle?: {
    vecType?: string;
    band?: string;
  };
  sourceData?: {
    freq?: number;
    colpc?: number;
    rowpc?: number;
    sig?: string | null;
  };
  // Position and neighbor information (populated by enrichGridWithPositionContext)
  position?: CellPosition;
  // Hierarchy information for provenance tracking (for data cells)
  hierarchy?: CellHierarchy;
  // Split cell tracking (for cells split by splitLargeCells)
  splitInfo?: SplitInfo;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate how many bands a side vector has
 */
export function calculateBandsForVec(vec: PlatinumVec, props?: PlatinumProperties, showSigBand?: boolean): number {
  // Base vecs: freq, colpc if showRedundant100
  if (vec.type === 'Base') {
    let bands = 1 // freq
    if (props?.cells?.columnPercents && props?.cells?.showRedundant100) {
      bands++
    }
    return bands
  }

  // Arith/Function vecs: freq only by default, but respect allowPC override
  if (vec.type === 'Arith' || vec.type === 'Function') {
    if (vec.allowPC) {
      let bands = 0
      if (props?.cells?.frequencies) bands++
      if (props?.cells?.columnPercents) bands++
      if (props?.cells?.rowPercents) bands++
      return Math.max(bands, 1)
    }
    return 1
  }

  // Spacer vecs
  if (vec.type === 'Spacer') {
    return 1
  }

  // Code/Net vecs: count visible bands
  let bands = 0
  if (props?.cells?.frequencies) bands++
  if (props?.cells?.columnPercents) bands++
  if (props?.cells?.rowPercents) bands++
  if (showSigBand) bands++

  return Math.max(bands, 1)
}

/**
 * Which side vecs get an extra significance band.
 *
 * A sig band is per-vec, not global: only vecs whose cells actually carry a
 * `sig` value grow one, and only when letters are not being appended inline.
 * `platinumToGrid` computes this inline; it is exported so that anything
 * reasoning about row geometry outside the renderer (see
 * `utils/tableViewConfig.ts`) counts the same rows the renderer will draw.
 *
 * NOTE `data.cells.rows` is indexed by SIDE VEC, not by band row — the bands
 * (freq/colpc/rowpc) are fields on each cell. One vec is one row of cells,
 * however many band rows it renders as.
 */
export function sigBandVecIndices(data: PlatinumData): Set<number> {
  const sigBandVecs = new Set<number>()
  const appendLetters = data.properties?.significance?.appendLetters ?? false
  if (appendLetters || !data.cells?.rows) return sigBandVecs
  for (let i = 0; i < data.cells.rows.length; i++) {
    if (data.cells.rows[i]?.cell?.some(c => c?.sig)) {
      sigBandVecs.add(i)
    }
  }
  return sigBandVecs
}

/** How many grid rows each side vec occupies, in vec order. */
export function sideVecBandCounts(data: PlatinumData): number[] {
  const sigBandVecs = sigBandVecIndices(data)
  return (data.side?.vecs || []).map((vec, i) =>
    calculateBandsForVec(vec, data.properties, sigBandVecs.has(i)))
}

/**
 * The number of grid rows above the first data row.
 *
 * Side group `y` coordinates are absolute grid rows, so this is the baseline
 * that converts a group's `y` into a band-row offset and back.
 */
export function computeHeaderRows(data: PlatinumData): number {
  const props = data.properties
  const topGroups = data.top?.groups
  const topThickness = topGroups && topGroups.length > 0
    ? Math.max(...topGroups.map(g => g.y ?? 0), 0) + 1
    : 0
  const topLabelRows = props?.top?.labels?.visible ? 1 : 0
  const topVecsHaveLetters = data.top?.vecs?.some(v => v?.letter && v.letter.trim() !== '') ?? false
  const appendLetters = props?.significance?.appendLetters ?? false
  const topLetterRows = (props?.significance?.letters === 'Column' ||
    (topVecsHaveLetters && !appendLetters)) ? 1 : 0
  return topThickness + topLabelRows + topLetterRows
}

/**
 * Side header width in *server* coordinates — before `platinumToGrid` drops
 * blank group columns. Top group `x` values are expressed against this, which
 * is why it is the number that matters when re-emitting them.
 */
export function computeHeaderCols(data: PlatinumData): number {
  const props = data.properties
  const sideGroups = data.side?.groups
  const sideThickness = sideGroups && sideGroups.length > 0
    ? Math.max(...sideGroups.map(g => g.x ?? 0), 0) + 1
    : 0
  const sideLabelCols = props?.side?.labels?.visible ? 1 : 0
  const sideVecsHaveLetters = data.side?.vecs?.some(v => v?.letter && v.letter.trim() !== '') ?? false
  const appendLetters = props?.significance?.appendLetters ?? false
  const sideLetterCols = (props?.significance?.letters === 'Row' ||
    (sideVecsHaveLetters && !appendLetters)) ? 1 : 0
  return sideThickness + sideLabelCols + sideLetterCols
}

/**
 * Get visible band names for a vector
 */
export function getVisibleBands(vec: PlatinumVec, props: PlatinumProperties, showSigBand?: boolean): string[] {
  // Base vecs
  if (vec.type === 'Base') {
    const bands = ['freq']
    if (props.cells?.columnPercents && props.cells?.showRedundant100) {
      bands.push('colpc')
    }
    return bands
  }

  // Arith/Function vecs: default to freq only, but respect allowPC override
  // When allowPC is true (set via % suffix in syntax), show colpc/rowpc bands
  if (vec.type === 'Arith' || vec.type === 'Function') {
    if (vec.allowPC) {
      const bands: string[] = []
      if (props.cells?.frequencies) bands.push('freq')
      if (props.cells?.columnPercents) bands.push('colpc')
      if (props.cells?.rowPercents) bands.push('rowpc')
      return bands.length > 0 ? bands : ['freq']
    }
    return ['freq']
  }

  // Spacer vecs
  if (vec.type === 'Spacer') {
    return ['empty']
  }

  // Code/Net vecs: all bands
  const bands: string[] = []
  if (props.cells?.frequencies) bands.push('freq')
  if (props.cells?.columnPercents) bands.push('colpc')
  if (props.cells?.rowPercents) bands.push('rowpc')
  if (showSigBand) bands.push('sig')

  return bands.length > 0 ? bands : ['freq']
}

/**
 * Get decimals for a cell based on side vector type and band.
 *
 * Logic (from Roland's clarification):
 * - Percent bands (colpc, rowpc) always use decimals.percents
 * - Freq band varies by vec type:
 *   - Function/Arith → decimals.expressions
 *   - Stat → decimals.statistics
 *   - Base/Code/Net/other → decimals.frequencies
 */
function getDecimalsForVecType(
  vecType: PVecType | string | undefined,
  band: string,
  decimals: XDecimals | undefined
): number {
  // Percent bands ALWAYS use percents decimals (regardless of vec type)
  if (band === 'colpc' || band === 'rowpc') {
    return decimals?.percents ?? 1
  }

  // Freq band varies by vec type
  switch (vecType) {
    case 'Function':
    case 'Arith':
      return decimals?.expressions ?? 3
    case 'Stat':
      return decimals?.statistics ?? 2
    default:
      return decimals?.frequencies ?? 0
  }
}

/**
 * Format cell value for display
 *
 * @param sourceData - Cell data containing freq, colpc, rowpc, sig
 * @param band - Band type ('freq', 'colpc', 'rowpc', 'stat1', 'stat2')
 * @param props - Platinum properties for formatting
 * @param sideVecType - Side vector type for decimal calculation
 * @param sideAllowPC - Whether percentages are allowed for this row (from sideVec.allowPC)
 * @param topAllowPC - Whether percentages are allowed for this column (from topVec.allowPC)
 */
export function formatCellValue(
  sourceData: GridCell['sourceData'],
  band: string,
  props: PlatinumProperties,
  sideVecType?: PVecType | string,
  sideAllowPC?: boolean,
  topAllowPC?: boolean
): string {
  if (!sourceData) return ''

  // Helper to append significance letters if enabled
  const maybeAppendSig = (formatted: string): string => {
    if (props.significance?.appendLetters && sourceData.sig) {
      return `${formatted} ${sourceData.sig}`
    }
    return formatted
  }

  switch (band) {
    case 'freq': {
      const decimals = getDecimalsForVecType(sideVecType, 'freq', props.decimals)
      const value = sourceData.freq ?? 0
      return maybeAppendSig(value.toFixed(decimals))
    }

    case 'colpc':
    case 'rowpc': {
      // Show percentages only if BOTH vectors allow it
      // undefined is treated as true (default for backward compatibility)
      const canShowPercent = sideAllowPC !== false && topAllowPC !== false

      // If either vector disallows percentages, show freq data formatted as freq
      if (!canShowPercent) {
        const decimals = getDecimalsForVecType(sideVecType, 'freq', props.decimals)
        const value = sourceData.freq ?? 0
        return maybeAppendSig(value.toFixed(decimals))
      }

      let value = band === 'colpc' ? (sourceData.colpc ?? 0) : (sourceData.rowpc ?? 0)

      if (!props.cells?.percentsAsProportions) {
        value *= 100
      }

      const decimals = getDecimalsForVecType(sideVecType, band, props.decimals)
      let formatted = value.toFixed(decimals)

      if (props.cells?.percentSign) {
        formatted += '%'
      }

      return maybeAppendSig(formatted)
    }

    case 'sig':
    case 'stat1':
    case 'stat2':
      return sourceData.sig ?? ''

    default:
      return ''
  }
}

/**
 * Get the actual cell at a position, resolving occupied cells to their source
 */
function getCellAtPosition(
  grid: GridCell[][],
  rowIdx: number,
  colIdx: number
): GridCell | null {
  if (rowIdx < 0 || rowIdx >= grid.length || colIdx < 0 || colIdx >= grid[0].length) {
    return null
  }

  const cell = grid[rowIdx][colIdx]
  if (!cell) return null

  // If it's an occupied cell, search for the source cell
  if (cell.type === 'occupied') {
    // Search upward and leftward to find the spanning cell
    for (let r = rowIdx; r >= 0; r--) {
      for (let c = colIdx; c >= 0; c--) {
        const sourceCell = grid[r][c]
        if (sourceCell && sourceCell.type !== 'occupied') {
          // Check if this cell spans into our target position
          if (r + sourceCell.rowspan > rowIdx && c + sourceCell.colspan > colIdx) {
            return sourceCell
          }
        }
      }
    }
    return null
  }

  return cell
}

/**
 * Enrich grid cells with position context and neighbor information
 */
function enrichGridWithPositionContext(
  grid: GridCell[][],
  headerRows: number,
  headerCols: number
): void {
  for (let rowIdx = 0; rowIdx < grid.length; rowIdx++) {
    for (let colIdx = 0; colIdx < grid[rowIdx].length; colIdx++) {
      const cell = grid[rowIdx][colIdx]

      // Skip occupied cells (they're part of merged cells)
      if (cell.type === 'occupied') continue

      // Determine region classification
      const isInTopHeaderRows = rowIdx < headerRows
      const isInSideHeaderCols = colIdx < headerCols
      const isCorner = isInTopHeaderRows && isInSideHeaderCols

      // Get neighbor cells (resolve occupied to source)
      const aboveCell = getCellAtPosition(grid, rowIdx - 1, colIdx)
      const belowCell = getCellAtPosition(grid, rowIdx + 1, colIdx)
      const leftCell = getCellAtPosition(grid, rowIdx, colIdx - 1)
      const rightCell = getCellAtPosition(grid, rowIdx, colIdx + 1)

      // Preserve existing isFirstInVector/isLastInVector for data cells
      // (these were set during data cell placement)
      const existingPosition = cell.position
      const isFirstInVector = existingPosition?.isFirstInVector ?? false
      const isLastInVector = existingPosition?.isLastInVector ?? false

      // Detect horizontal vector boundaries (for data cells)
      let isFirstColumnInTopVector = false
      let isLastColumnInTopVector = false

      if (cell.hierarchy && leftCell) {
        // Check if left neighbor is in a different top vector (or no vector)
        const currentTopVecIdx = cell.hierarchy.topVecIndex
        const leftTopVecIdx = leftCell.hierarchy?.topVecIndex

        if (currentTopVecIdx !== undefined && currentTopVecIdx !== leftTopVecIdx) {
          isFirstColumnInTopVector = true
        }
      } else if (cell.hierarchy && !leftCell) {
        // No left neighbor means we're at the start
        isFirstColumnInTopVector = true
      }

      if (cell.hierarchy && rightCell) {
        // Check if right neighbor is in a different top vector (or no vector)
        const currentTopVecIdx = cell.hierarchy.topVecIndex
        const rightTopVecIdx = rightCell.hierarchy?.topVecIndex

        if (currentTopVecIdx !== undefined && currentTopVecIdx !== rightTopVecIdx) {
          isLastColumnInTopVector = true
        }
      } else if (cell.hierarchy && !rightCell) {
        // No right neighbor means we're at the end
        isLastColumnInTopVector = true
      }

      // Override for side headers: if this is a side header and it's a group or label,
      // check if the cell above is in a different side vector (for side vector group boundaries)
      let isFirstRowInSideVector = false
      if ((cell.type === 'group' || cell.type === 'label') && isInSideHeaderCols && aboveCell) {
        // If above cell is data or in header row, this is the start of a side vector group
        if (aboveCell.type === 'data' || rowIdx - 1 < headerRows) {
          isFirstRowInSideVector = true
        }
        // Check if above cell is group/label for different side vector
        else if ((aboveCell.type === 'group' || aboveCell.type === 'label') &&
                 aboveCell.hierarchy && cell.hierarchy) {
          const currentSideVecIdx = cell.hierarchy.sideVecIndex
          const aboveSideVecIdx = aboveCell.hierarchy.sideVecIndex
          if (currentSideVecIdx !== undefined && currentSideVecIdx !== aboveSideVecIdx) {
            isFirstRowInSideVector = true
          }
        }
      } else if ((cell.type === 'group' || cell.type === 'label') && isInSideHeaderCols && !aboveCell) {
        // No above cell means we're at the very top
        isFirstRowInSideVector = true
      }

      // Populate position context
      cell.position = {
        rowIdx,
        colIdx,
        isTopHeader: isInTopHeaderRows,
        isSideHeader: isInSideHeaderCols,
        isCorner,
        isFirstInVector,
        isLastInVector,
        isFirstColumnInTopVector,
        isLastColumnInTopVector,
        neighbors: {
          above: aboveCell?.type ?? null,
          below: belowCell?.type ?? null,
          left: leftCell?.type ?? null,
          right: rightCell?.type ?? null,
        },
      }
    }
  }
}

/**
 * Place cell in grid and mark occupied cells
 */
function placeCell(
  grid: GridCell[][],
  row: number,
  col: number,
  cell: GridCell
): void {
  if (row < 0 || row >= grid.length || col < 0 || col >= grid[0].length) {
    return
  }

  grid[row][col] = cell

  // Mark spanned cells as occupied
  for (let r = row; r < Math.min(row + cell.rowspan, grid.length); r++) {
    for (let c = col; c < Math.min(col + cell.colspan, grid[0].length); c++) {
      if (r !== row || c !== col) {
        grid[r][c] = {
          content: '',
          rowspan: 1,
          colspan: 1,
          type: 'occupied'
        }
      }
    }
  }
}

/**
 * Split cells with large rowspan/colspan into multiple smaller cells
 * for better virtualized rendering performance.
 *
 * @param grid - The grid to modify
 */
function splitLargeCells(grid: GridCell[][]): void {
  const rows = grid.length
  if (rows === 0) return
  const cols = grid[0].length

  // Track cells to split (can't modify grid while iterating)
  const cellsToSplit: Array<{ rowIdx: number; colIdx: number; cell: GridCell }> = []

  // Find all cells that need splitting
  for (let rowIdx = 0; rowIdx < rows; rowIdx++) {
    for (let colIdx = 0; colIdx < cols; colIdx++) {
      const cell = grid[rowIdx][colIdx]

      // Skip occupied cells and cells that don't need splitting
      if (cell.type === 'occupied') continue
      if (cell.rowspan <= MAX_ROWSPAN && cell.colspan <= MAX_COLSPAN) continue

      cellsToSplit.push({ rowIdx, colIdx, cell })
    }
  }

  // Split each identified cell
  for (const { rowIdx, colIdx, cell } of cellsToSplit) {
    const numRowSplits = Math.ceil(cell.rowspan / MAX_ROWSPAN)
    const numColSplits = Math.ceil(cell.colspan / MAX_COLSPAN)

    // Generate UUID for this original cell
    const originalCellId = crypto.randomUUID()

    // Clear the original cell's occupied markers
    for (let r = rowIdx; r < Math.min(rowIdx + cell.rowspan, rows); r++) {
      for (let c = colIdx; c < Math.min(colIdx + cell.colspan, cols); c++) {
        if (grid[r][c].type === 'occupied') {
          // Will be re-marked by split cells
          grid[r][c] = {
            content: '',
            rowspan: 1,
            colspan: 1,
            type: 'empty'
          }
        }
      }
    }

    // Create split cells
    let currentRow = rowIdx
    for (let rowSplit = 0; rowSplit < numRowSplits; rowSplit++) {
      // Calculate rowspan for this split
      const remainingRows = (rowIdx + cell.rowspan) - currentRow
      const splitRowspan = Math.min(MAX_ROWSPAN, remainingRows)

      let currentCol = colIdx
      for (let colSplit = 0; colSplit < numColSplits; colSplit++) {
        // Calculate colspan for this split
        const remainingCols = (colIdx + cell.colspan) - currentCol
        const splitColspan = Math.min(MAX_COLSPAN, remainingCols)

        // Create duplicate cell with adjusted span and split tracking
        const splitCell: GridCell = {
          ...cell,
          rowspan: splitRowspan,
          colspan: splitColspan,
          splitInfo: {
            originalCellId,
            isFirstRow: rowSplit === 0,
            isLastRow: rowSplit === numRowSplits - 1,
            isFirstCol: colSplit === 0,
            isLastCol: colSplit === numColSplits - 1
          }
        }

        // Place the split cell
        placeCell(grid, currentRow, currentCol, splitCell)

        currentCol += splitColspan
      }
      currentRow += splitRowspan
    }
  }
}

/**
 * Convert PlatinumData to 2D grid
 * Faithfully renders server-provided coordinates
 *
 * @param data - PlatinumData from server
 * @param debug - If true, preserves exact server coordinates without transformations.
 *                If false, applies cell-splitting for large rowspan/colspan values.
 */
export function platinumToGrid(data: PlatinumData, debug: boolean = false): GridCell[][] {
  const props = data.properties

  // Calculate grid dimensions
  const titleRows = 0

  const topGroups = data.top?.groups
  const topThickness = topGroups && topGroups.length > 0
    ? Math.max(...topGroups.map(g => g.y ?? 0), 0) + 1
    : 0

  const topLabelRows = props?.top?.labels?.visible ? 1 : 0
  // Show letter row when: letters='Column' explicitly, OR the vecs actually
  // contain letter data and letters aren't being appended inline to cells.
  const topVecsHaveLetters = data.top?.vecs?.some(v => v?.letter && v.letter.trim() !== '') ?? false
  const sideVecsHaveLetters = data.side?.vecs?.some(v => v?.letter && v.letter.trim() !== '') ?? false
  const appendLetters = props?.significance?.appendLetters ?? false
  const topLetterRows = (props?.significance?.letters === 'Column' ||
    (topVecsHaveLetters && !appendLetters)) ? 1 : 0

  const sideGroups = data.side?.groups
  const originalSideThickness = sideGroups && sideGroups.length > 0
    ? Math.max(...sideGroups.map(g => g.x ?? 0), 0) + 1
    : 0

  // Identify and filter out empty group columns
  const columnAdjustment = identifyEmptyGroupColumns(sideGroups, originalSideThickness, debug)
  const sideThickness = originalSideThickness - columnAdjustment.emptyColumns.size

  const sideLabelCols = props?.side?.labels?.visible ? 1 : 0
  const sideLetterCols = (props?.significance?.letters === 'Row' ||
    (sideVecsHaveLetters && !appendLetters)) ? 1 : 0

  // Per-vec sig band: only show a sig row for side vecs that actually have sig data
  const sigBandVecs = sigBandVecIndices(data)

  const headerRows = titleRows + topThickness + topLabelRows + topLetterRows
  const headerCols = sideThickness + sideLabelCols + sideLetterCols

  let dataRows = 0
  const sideVecs = data.side?.vecs
  if (sideVecs) {
    for (let i = 0; i < sideVecs.length; i++) {
      dataRows += calculateBandsForVec(sideVecs[i], props, sigBandVecs.has(i))
    }
  }

  const dataCols = data.top?.vecs?.length ?? 0

  const totalRows = headerRows + dataRows
  const totalCols = headerCols + dataCols

  // Initialize empty grid
  const grid: GridCell[][] = Array(totalRows).fill(null).map(() =>
    Array(totalCols).fill(null).map(() => ({
      content: '',
      rowspan: 1,
      colspan: 1,
      type: 'empty' as const
    }))
  )

  // Place top groups
  if (props?.top?.groups?.visible && topGroups) {
    for (const group of topGroups) {
      const row = group.y ?? 0
      const originalCol = group.x ?? 0
      const originalWidth = group.w ?? 1

      let adjustedCol: number
      let adjustedWidth: number

      // In debug mode, preserve exact server coordinates without adjustment
      if (debug) {
        adjustedCol = originalCol
        adjustedWidth = originalWidth
      } else {
        // Adjust coordinates based on removed empty columns
        const originalEndCol = originalCol + originalWidth
        const sideHeaderEndCol = originalSideThickness

        if (originalEndCol <= sideHeaderEndCol) {
          // Case 1: Group entirely in side header area
          // Use column mapping like side groups do
          const mappedCol = columnAdjustment.columnMapping.get(originalCol)
          if (mappedCol === undefined) {
            continue // Skip if starts in removed column
          }
          adjustedCol = mappedCol

          // Calculate adjusted width (count non-empty columns)
          adjustedWidth = 0
          for (let col = originalCol; col < originalEndCol; col++) {
            if (!columnAdjustment.emptyColumns.has(col)) {
              adjustedWidth++
            }
          }
        } else if (originalCol >= sideHeaderEndCol) {
          // Case 2: Group entirely in data area
          // Shift left by number of removed columns
          const shift = columnAdjustment.emptyColumns.size
          adjustedCol = originalCol - shift
          adjustedWidth = originalWidth // Width unchanged
        } else {
          // Case 3: Group spans both side header and data areas
          // Map the side header portion, preserve data portion
          const mappedCol = columnAdjustment.columnMapping.get(originalCol)
          if (mappedCol === undefined) {
            // If starts in removed column, find first non-empty column
            adjustedCol = sideThickness // Default to start of data area
            for (let col = originalCol; col < sideHeaderEndCol; col++) {
              const mapped = columnAdjustment.columnMapping.get(col)
              if (mapped !== undefined) {
                adjustedCol = mapped
                break
              }
            }
          } else {
            adjustedCol = mappedCol
          }

          // Calculate width: count non-empty columns in side header portion + data portion
          let widthInSideHeader = 0
          for (let col = originalCol; col < Math.min(originalEndCol, sideHeaderEndCol); col++) {
            if (!columnAdjustment.emptyColumns.has(col)) {
              widthInSideHeader++
            }
          }
          const widthInDataArea = Math.max(0, originalEndCol - sideHeaderEndCol)
          adjustedWidth = widthInSideHeader + widthInDataArea
        }
      }

      // Only place cell if it has non-zero width
      if (adjustedWidth > 0) {
        placeCell(grid, row, adjustedCol, {
          content: group.text ?? '',
          rowspan: group.h ?? 1,
          colspan: adjustedWidth,
          type: 'group',
          x: adjustedCol,
          y: group.y,
          w: adjustedWidth,
          h: group.h
        })
      }
    }
  }

  // Place top labels
  const topVecs = data.top?.vecs
  if (props?.top?.labels?.visible && topVecs) {
    const labelRow = titleRows + topThickness

    for (let i = 0; i < topVecs.length; i++) {
      const col = headerCols + i
      const vec = topVecs[i]
      const labelContent = vec?.label ?? ''

      placeCell(grid, labelRow, col, {
        content: labelContent,
        rowspan: 1,
        colspan: 1,
        type: labelContent.trim() === '' ? 'empty' : 'label',
        x: col,
        y: labelRow,
        w: 1,
        h: 1
      })
    }
  }

  // Place top letters
  if (topLetterRows > 0 && topVecs) {
    const letterRow = titleRows + topThickness + topLabelRows

    for (let i = 0; i < topVecs.length; i++) {
      const col = headerCols + i
      const letter = topVecs[i]?.letter ?? ''

      placeCell(grid, letterRow, col, {
        content: letter,
        rowspan: 1,
        colspan: 1,
        type: letter.trim() === '' ? 'empty' : 'letter',
        x: col,
        y: letterRow,
        w: 1,
        h: 1
      })
    }
  }

  // Place side groups
  if (sideGroups) {
    for (const group of sideGroups) {
      // Skip groups with blank content (they were identified as empty)
      if (!group.text || group.text.trim() === '') {
        continue
      }

      const originalCol = group.x ?? 0
      const adjustedCol = columnAdjustment.columnMapping.get(originalCol)

      // Skip if this column was filtered out
      if (adjustedCol === undefined) {
        continue
      }

      const row = group.y ?? 0

      // Calculate adjusted width (some columns may have been removed)
      const originalEndCol = originalCol + (group.w ?? 1)
      let adjustedWidth = 0
      for (let col = originalCol; col < originalEndCol; col++) {
        if (!columnAdjustment.emptyColumns.has(col)) {
          adjustedWidth++
        }
      }

      // Only place if there's actual width remaining
      if (adjustedWidth > 0) {
        placeCell(grid, row, adjustedCol, {
          content: group.text ?? '',
          rowspan: group.h ?? 1,
          colspan: adjustedWidth,
          type: 'group',
          x: adjustedCol,
          y: group.y,
          w: adjustedWidth,
          h: group.h
        })
      }
    }
  }

  // Place side labels
  if (props?.side?.labels?.visible && sideVecs) {
    let currentRow = headerRows

    for (let i = 0; i < sideVecs.length; i++) {
      const vec = sideVecs[i]
      const bandsForVec = calculateBandsForVec(vec, props, sigBandVecs.has(i))
      const labelCol = sideThickness
      const labelContent = vec?.label ?? ''

      placeCell(grid, currentRow, labelCol, {
        content: labelContent,
        rowspan: bandsForVec,
        colspan: 1,
        type: labelContent.trim() === '' ? 'empty' : 'label',
        x: labelCol,
        y: currentRow,
        w: 1,
        h: bandsForVec
      })

      currentRow += bandsForVec
    }
  }

  // Place side letters
  if (sideLetterCols > 0 && sideVecs) {
    let currentRow = headerRows

    for (let i = 0; i < sideVecs.length; i++) {
      const vec = sideVecs[i]
      const bandsForVec = calculateBandsForVec(vec, props, sigBandVecs.has(i))
      const letterCol = sideThickness + sideLabelCols
      const letter = vec?.letter ?? ''

      placeCell(grid, currentRow, letterCol, {
        content: letter,
        rowspan: bandsForVec,
        colspan: 1,
        type: letter.trim() === '' ? 'empty' : 'letter',
        x: letterCol,
        y: currentRow,
        w: 1,
        h: bandsForVec
      })

      currentRow += bandsForVec
    }
  }

  // Place data cells
  let rowIdx = headerRows

  if (sideVecs && topVecs && props) {
    for (let sideIdx = 0; sideIdx < sideVecs.length; sideIdx++) {
      const sideVec = sideVecs[sideIdx]
      const bands = getVisibleBands(sideVec, props, sigBandVecs.has(sideIdx))

      for (let bandIdx = 0; bandIdx < bands.length; bandIdx++) {
        const bandName = bands[bandIdx]
        const isFirstBand = bandIdx === 0
        const isLastBand = bandIdx === bands.length - 1

        for (let topIdx = 0; topIdx < topVecs.length; topIdx++) {
          const col = headerCols + topIdx
          const topVec = topVecs[topIdx]

          const rows = data.cells?.rows
          const cellData = rows?.[sideIdx]?.cell?.[topIdx]

          if (topVec?.type === 'Spacer') {
            placeCell(grid, rowIdx, col, {
              content: '',
              rowspan: 1,
              colspan: 1,
              type: 'empty',
              bandStyle: {
                vecType: 'Spacer',
                band: 'empty'
              },
              x: col,
              y: rowIdx,
              w: 1,
              h: 1,
              hierarchy: {
                topVecIndex: topIdx,
                sideVecIndex: sideIdx,
                bandIndex: bandIdx
              },
              position: {
                rowIdx,
                colIdx: col,
                isTopHeader: false,
                isSideHeader: false,
                isCorner: false,
                isFirstInVector: isFirstBand,
                isLastInVector: isLastBand,
                isFirstColumnInTopVector: false,  // Will be set by enrichGridWithPositionContext
                isLastColumnInTopVector: false,   // Will be set by enrichGridWithPositionContext
                neighbors: {
                  above: null,
                  below: null,
                  left: null,
                  right: null
                }
              }
            })
          } else {
            const cellContent = cellData && props
              ? formatCellValue(cellData, bandName, props, sideVec?.type, sideVec?.allowPC, topVec?.allowPC)
              : ''

            placeCell(grid, rowIdx, col, {
              content: cellContent,
              rowspan: 1,
              colspan: 1,
              type: 'data',
              bandStyle: {
                vecType: sideVec?.type,
                band: bandName
              },
              sourceData: cellData ? {
                freq: cellData.freq,
                colpc: cellData.colpc,
                rowpc: cellData.rowpc,
                sig: cellData.sig
              } : undefined,
              x: col,
              y: rowIdx,
              w: 1,
              h: 1,
              hierarchy: {
                topVecIndex: topIdx,
                sideVecIndex: sideIdx,
                bandIndex: bandIdx
              },
              position: {
                rowIdx,
                colIdx: col,
                isTopHeader: false,
                isSideHeader: false,
                isCorner: false,
                isFirstInVector: isFirstBand,
                isLastInVector: isLastBand,
                isFirstColumnInTopVector: false,  // Will be set by enrichGridWithPositionContext
                isLastColumnInTopVector: false,   // Will be set by enrichGridWithPositionContext
                neighbors: {
                  above: null,
                  below: null,
                  left: null,
                  right: null
                }
              }
            })
          }
        }

        rowIdx++
      }
    }
  }

  // Split large cells for virtualized rendering (only in production mode)
  if (!debug) {
    splitLargeCells(grid)
  }

  // Enrich grid with position context and neighbor information
  enrichGridWithPositionContext(grid, headerRows, headerCols)

  return grid
}

// ============================================================================
// HIERARCHY/PROVENANCE EXTRACTION
// ============================================================================

/**
 * Provenance information for a cell
 * Shows the hierarchy path for both top and side axes
 */
export interface CellProvenanceInfo {
  topHierarchy: Array<{
    type: 'group' | 'label' | 'letter';
    text: string;
  }>;
  sideHierarchy: Array<{
    type: 'group' | 'label' | 'letter' | 'band';
    text: string;
  }>;
  cellData: {
    freq?: number;
    colpc?: number;
    rowpc?: number;
    sig?: string | null;
  };
  bandType: string;
  vecType?: string;
}

/**
 * Helper to find a cell that spans into a given column
 */
function findSpanningCellHorizontal(
  grid: GridCell[][],
  rowIdx: number,
  targetCol: number
): GridCell | null {
  // Search backwards from targetCol to find the cell that spans into it
  for (let colIdx = targetCol; colIdx >= 0; colIdx--) {
    const cell = grid[rowIdx]?.[colIdx]
    if (!cell || cell.type === 'occupied') continue

    // Check if this cell's colspan covers the target column
    if (colIdx + cell.colspan > targetCol) {
      return cell
    }

    // If we found a cell that doesn't span to our target, stop searching
    break
  }
  return null
}

/**
 * Helper to find a cell that spans into a given row
 */
function findSpanningCellVertical(
  grid: GridCell[][],
  targetRow: number,
  colIdx: number
): GridCell | null {
  // Search backwards from targetRow to find the cell that spans into it
  for (let rowIdx = targetRow; rowIdx >= 0; rowIdx--) {
    const cell = grid[rowIdx]?.[colIdx]
    if (!cell || cell.type === 'occupied') continue

    // Check if this cell's rowspan covers the target row
    if (rowIdx + cell.rowspan > targetRow) {
      return cell
    }

    // If we found a cell that doesn't span to our target, stop searching
    break
  }
  return null
}

/**
 * Extract hierarchy information for a cell
 *
 * This function returns the full provenance path for a data cell,
 * including all groups, labels, letters, and the band type.
 *
 * @param grid The 2D grid
 * @param rowIdx Row index of the cell
 * @param colIdx Column index of the cell
 * @param data Original PlatinumData
 * @returns Provenance information or null if not a data cell
 */
export function getCellHierarchy(
  grid: GridCell[][],
  rowIdx: number,
  colIdx: number,
  data: PlatinumData
): CellProvenanceInfo | null {
  const cell = grid[rowIdx]?.[colIdx]
  if (!cell || cell.type !== 'data') return null

  const props = data.properties

  // Calculate header dimensions
  const topGroups = data.top?.groups
  const topThickness = topGroups && topGroups.length > 0
    ? Math.max(...topGroups.map(g => g.y ?? 0), 0) + 1
    : 0
  const topLabelRows = props?.top?.labels?.visible ? 1 : 0
  const topVecsHaveLettersH = data.top?.vecs?.some(v => v?.letter && v.letter.trim() !== '') ?? false
  const appendLettersH = props?.significance?.appendLetters ?? false
  const topLetterRows = (props?.significance?.letters === 'Column' ||
    (topVecsHaveLettersH && !appendLettersH)) ? 1 : 0

  const sideGroups = data.side?.groups
  const sideThickness = sideGroups && sideGroups.length > 0
    ? Math.max(...sideGroups.map(g => g.x ?? 0), 0) + 1
    : 0
  const sideLabelCols = props?.side?.labels?.visible ? 1 : 0

  // Extract top axis hierarchy (column path)
  const topHierarchy: Array<{ type: 'group' | 'label' | 'letter'; text: string }> = []

  // Get groups (multiple rows possible, can have colspan)
  for (let groupRow = 0; groupRow < topThickness; groupRow++) {
    const groupCell = findSpanningCellHorizontal(grid, groupRow, colIdx)
    if (groupCell && groupCell.type === 'group' && groupCell.content.trim()) {
      topHierarchy.push({
        type: 'group',
        text: groupCell.content
      })
    }
  }

  // Get label
  const labelRow = topThickness
  const topLabelCell = grid[labelRow]?.[colIdx]
  if (topLabelCell && topLabelCell.type === 'label' && topLabelCell.content.trim()) {
    topHierarchy.push({
      type: 'label',
      text: topLabelCell.content
    })
  }

  // Get letter (if enabled)
  if (topLetterRows > 0) {
    const letterRow = topThickness + topLabelRows
    const letterCell = grid[letterRow]?.[colIdx]
    if (letterCell && letterCell.type === 'letter' && letterCell.content.trim()) {
      topHierarchy.push({
        type: 'letter',
        text: letterCell.content
      })
    }
  }

  // Extract side axis hierarchy (row path)
  const sideHierarchy: Array<{ type: 'group' | 'label' | 'letter' | 'band'; text: string }> = []

  // Get groups (multiple columns possible, can have rowspan)
  for (let groupCol = 0; groupCol < sideThickness; groupCol++) {
    const groupCell = findSpanningCellVertical(grid, rowIdx, groupCol)
    if (groupCell && groupCell.type === 'group' && groupCell.content.trim()) {
      sideHierarchy.push({
        type: 'group',
        text: groupCell.content
      })
    }
  }

  // Get label (can have rowspan for multi-band rows)
  const labelCol = sideThickness
  const sideLabelCell = findSpanningCellVertical(grid, rowIdx, labelCol)
  if (sideLabelCell && sideLabelCell.type === 'label' && sideLabelCell.content.trim()) {
    sideHierarchy.push({
      type: 'label',
      text: sideLabelCell.content
    })
  }

  // Get letter (if enabled, can have rowspan)
  const sideVecsHaveLettersH = data.side?.vecs?.some(v => v?.letter && v.letter.trim() !== '') ?? false
  const showSideLetters = props?.significance?.letters === 'Row' ||
    (sideVecsHaveLettersH && !appendLettersH)
  if (showSideLetters) {
    const letterCol = sideThickness + sideLabelCols
    const letterCell = findSpanningCellVertical(grid, rowIdx, letterCol)
    if (letterCell && letterCell.type === 'letter' && letterCell.content.trim()) {
      sideHierarchy.push({
        type: 'letter',
        text: letterCell.content
      })
    }
  }

  // Add band information
  if (cell.bandStyle?.band) {
    const bandNames: Record<string, string> = {
      freq: 'Frequency',
      colpc: 'Column %',
      rowpc: 'Row %',
      sig: 'Significance',
      stat1: 'Significance',
      stat2: 'Significance'
    }
    sideHierarchy.push({
      type: 'band',
      text: bandNames[cell.bandStyle.band] || cell.bandStyle.band
    })
  }

  // Get cell data
  const cellData = cell.sourceData || {}
  const bandType = cell.bandStyle?.band || 'unknown'
  const vecType = cell.bandStyle?.vecType

  return {
    topHierarchy,
    sideHierarchy,
    cellData,
    bandType,
    vecType
  }
}
